import { memo, useId, useState } from 'react'
import { Sparkles, X } from 'lucide-react'
import { Button } from '../common'
import { legLabel } from '../leg-search/legModel'
import { americanToDecimal, formatAmericanOdds, formatDecimalOdds } from '../../utils/odds'
import styles from './AiSuggestedBets.module.css'

const LEADING_SIGN_PATTERN = /^[+-]/

const sourceLabels = { video: 'Video analyzed', 'video+image': 'Video and image analyzed', text: 'Post text analyzed', image: 'Image analyzed' }
const sourceWords = { text: 'from the post text', transcript: 'from the transcript', image: 'from an image' }

function suggestionOutcomeId(suggestion) {
  return String(suggestion?.result?.selection_ref?.external_outcome_id || '')
}

function text(value) {
  return String(value ?? '').trim()
}

// A mention yields up to three catalog matches, sent with the mention's
// position (mention_index). Groups keep the order of their first row, and the
// suggestions arrive best match first, so a group's first row is its primary
// one. Without mention_index (an older API) every row is its own bet: labels
// do not tell mentions apart (#870 had nine rows labelled same_game_parlay).
function groupSuggestions(suggestions) {
  const groups = []
  const byMention = new Map()
  suggestions.forEach((suggestion, index) => {
    const mentionIndex = suggestion?.mention_index
    const row = { suggestion, index }
    const known = Number.isInteger(mentionIndex) ? byMention.get(mentionIndex) : null
    if (known) {
      known.rows.push(row)
      return
    }
    // Keyed on the primary match, so a toggle left open does not carry over
    // to another bet after "Find bets again".
    const group = { key: suggestionOutcomeId(suggestion) || `${Number.isInteger(mentionIndex) ? mentionIndex : 'row'}-${index}`, rows: [row] }
    if (Number.isInteger(mentionIndex)) byMention.set(mentionIndex, group)
    groups.push(group)
  })
  return groups
}

// A price as the leg search shows it. A signed value or one of 100 or more
// is American ("+123", "-275"); one between 1 and 100 with a decimal point is
// decimal ("1.91"); words ("EVEN") show as they are. Anything else is no
// price.
function readPrice(value) {
  const raw = text(value)
  if (!raw) return null
  const number = Number(raw)
  if (!Number.isFinite(number)) return { text: raw, decimal: null }
  if (LEADING_SIGN_PATTERN.test(raw) || Math.abs(number) >= 100) {
    const american = formatAmericanOdds(number)
    return american ? { text: american, decimal: americanToDecimal(american) } : null
  }
  if (raw.includes('.') && number > 1 && number < 100) return { text: formatDecimalOdds(number), decimal: number }
  return null
}

// Today's price: the American odds, else the decimal odds.
function currentPrice(selection) {
  return readPrice(selection?.odds_american)?.text || formatDecimalOdds(selection?.odds_decimal)
}

// The price the content quoted, when it differs from today's by more than
// 2% as decimal odds, whichever format either came in. A quote or a current
// price that has no number (a worded price, no price) cannot be compared,
// so the quote is left out.
function quotedPrice(suggestion) {
  const quoted = readPrice(suggestion?.mentioned_odds)
  const selection = suggestion?.result?.selection
  const current = readPrice(selection?.odds_american)?.decimal || (Number(selection?.odds_decimal) > 1 ? Number(selection.odds_decimal) : null)
  if (!quoted?.decimal || !current) return ''
  return Math.abs(quoted.decimal - current) / current > 0.02 ? quoted.text : ''
}

// The matched selection with its line ("Harry Kane", "Arsenal Over 0.5",
// "Cincinnati Bengals +3.5"); a match without a selection keeps the model's
// label.
function suggestionHeadline(suggestion) {
  const result = suggestion?.result || {}
  const selectionName = text(result.selection?.name)
  const marketName = text(result.market?.name)
  if (!selectionName) return text(suggestion?.label) || marketName || 'Suggested bet'
  return legLabel({ selection_name: selectionName, line: result.market?.line, market_label: marketName, market_family: text(result.market?.family) })
}

// What the content said: the model's label when it adds to the headline,
// where it was found, and the price it quoted when that is not today's.
function mentionText(suggestion, headline) {
  const label = text(suggestion?.label)
  const evidence = text(suggestion?.evidence)
  const parts = []
  if (label && label.toLowerCase() !== headline.toLowerCase()) parts.push(`Mentioned as “${label}”`)
  if (evidence) parts.push(`${parts.length ? 'from' : 'From'} ${text(suggestion?.source) || 'post'}: ${evidence}`)
  let line = parts.join(' · ')
  const quoted = quotedPrice(suggestion)
  if (quoted) line = line ? `${line}, quoted at ${quoted}` : `Quoted at ${quoted}`
  return line
}

function SuggestionRow({ suggestion, attached, onAttach, onDismiss, nested = false }) {
  const result = suggestion?.result || {}
  const headline = suggestionHeadline(suggestion)
  const marketName = text(result.market?.name)
  const meta = [marketName === headline ? '' : marketName, text(result.event?.name)].filter(Boolean).join(' · ')
  const odds = currentPrice(result.selection)
  const mention = mentionText(suggestion, headline)
  return (
    <article className={`${styles.row} ${nested ? styles.nestedRow : ''}`.trim()}>
      <div className={styles.details}>
        <strong>{headline}</strong>
        {meta ? <span>{meta}</span> : null}
        {odds ? <span className={`${styles.odds} ${odds.startsWith('+') ? styles.positiveOdds : ''}`.trim()}>{odds}</span> : null}
        {mention ? <span className={styles.evidence}>{mention}</span> : null}
      </div>
      <span className={`${styles.confidence} ${styles[`confidence${suggestion.confidence || 'low'}`] || ''}`}>
        {suggestion.confidence ? `${suggestion.confidence[0].toUpperCase()}${suggestion.confidence.slice(1)} confidence` : 'Confidence unavailable'}
      </span>
      <div className={styles.actions}>
        <Button type="button" size="sm" onClick={() => onAttach?.(suggestion)} disabled={attached}>{attached ? 'Attached' : 'Attach bet'}</Button>
        {!attached ? <button type="button" className={styles.dismiss} onClick={onDismiss}><X size={14} aria-hidden="true" /> Dismiss</button> : null}
      </div>
    </article>
  )
}

function rowKey({ suggestion, index }) {
  return suggestionOutcomeId(suggestion) || `${text(suggestion?.label)}-${index}`
}

// One mention: its best match, with the other matches behind a toggle.
// Dismissing the best match dismisses the whole bet, so a hidden match is
// never promoted in its place (a match already attached stays, as it has no
// Dismiss of its own); dismissing another match drops only that one.
function SuggestionGroup({ rows, attached, onAttach, onDismiss }) {
  const [expanded, setExpanded] = useState(false)
  const moreId = useId()
  const [primary, ...more] = rows
  if (expanded && !more.length) setExpanded(false)
  const isAttached = ({ suggestion }) => attached.has(suggestionOutcomeId(suggestion))
  const dismissBet = () => rows.filter((row) => !isAttached(row)).forEach((row) => onDismiss?.(row.suggestion))
  return (
    <div className={styles.group}>
      <SuggestionRow suggestion={primary.suggestion} attached={isAttached(primary)} onAttach={onAttach} onDismiss={dismissBet} />
      {more.length ? (
        <>
          <button type="button" className={styles.moreToggle} aria-expanded={expanded} aria-controls={expanded ? moreId : undefined} onClick={() => setExpanded((open) => !open)}>
            {expanded ? 'Hide matches' : `Show ${more.length} more ${more.length === 1 ? 'match' : 'matches'}`}
          </button>
          {expanded ? (
            <div id={moreId} className={styles.moreList}>
              {more.map((row) => (
                <SuggestionRow key={rowKey(row)} suggestion={row.suggestion} attached={isAttached(row)} onAttach={onAttach} onDismiss={() => onDismiss?.(row.suggestion)} nested />
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  )
}

// The bets the content names that no market matched, with the words that
// name them: the card says what it read instead of "No bets found" (#870).
function UnmatchedMentions({ unmatched }) {
  if (!unmatched.length) return null
  return (
    <div className={styles.unmatched}>
      <p>{unmatched.length === 1 ? 'Found in this post, but no market matched:' : `Found ${unmatched.length} bets in this post that no market matched:`}</p>
      <ul>
        {unmatched.map((mention, index) => (
          <li key={`${text(mention?.label)}-${index}`}>
            <strong>{text(mention?.label) || 'Unnamed bet'}</strong>
            {text(mention?.evidence) ? <span className={styles.evidence}> · {sourceWords[mention.source] || 'from content'}: “{text(mention.evidence)}”</span> : null}
          </li>
        ))}
      </ul>
    </div>
  )
}

function AiSuggestedBets({
  suggestions = [],
  unmatched = [],
  warnings = [],
  source = '',
  status = 'idle',
  attachedOutcomeIds = [],
  onAttach,
  onDismiss,
  onFindBets,
  findDisabledReason = '',
}) {
  const attached = new Set((attachedOutcomeIds || []).map(String))
  const hasSuggestions = suggestions.length > 0
  const groups = groupSuggestions(suggestions)
  const matches = suggestions.length > groups.length ? ` (${suggestions.length} matches)` : ''
  return (
    <section className={styles.card} aria-labelledby="ai-suggested-bets-heading" data-testid="ai-suggested-bets">
      <header className={styles.header}>
        <div className={styles.titleRow}>
          <Sparkles size={16} aria-hidden="true" />
          <h3 id="ai-suggested-bets-heading">AI Suggested Bets</h3>
          <span className={styles.internalBadge}>Wager only</span>
          {sourceLabels[source] ? <span className={styles.sourceBadge}>{sourceLabels[source]}</span> : null}
        </div>
        {hasSuggestions ? <p className={styles.count}>{groups.length} {groups.length === 1 ? 'bet' : 'bets'} found from this content{matches}</p> : null}
        {hasSuggestions ? <Button type="button" size="sm" onClick={onFindBets} disabled={Boolean(findDisabledReason) || status === 'loading'} title={findDisabledReason || undefined}>Find bets again</Button> : null}
      </header>
      {status === 'loading' ? <p className={styles.status}>Finding bets from this post…</p> : null}
      {status === 'error' ? <p className={styles.status}>Bets could not be found right now. Try again.</p> : null}
      {hasSuggestions ? (
        <div className={styles.list}>
          {groups.map((group) => (
            <SuggestionGroup key={group.key} rows={group.rows} attached={attached} onAttach={onAttach} onDismiss={onDismiss} />
          ))}
          <UnmatchedMentions unmatched={unmatched} />
        </div>
      ) : (
        <div className={styles.empty}>
          {unmatched.length ? <UnmatchedMentions unmatched={unmatched} /> : <p>{source ? 'No bets found from this content.' : 'Find the bets discussed in this post.'}</p>}
          <Button type="button" size="sm" onClick={onFindBets} disabled={Boolean(findDisabledReason) || status === 'loading'} title={findDisabledReason || undefined}>Find bets from this post</Button>
        </div>
      )}
      {warnings.map((warning) => <p key={warning} className={styles.status} role="status">{warning}</p>)}
      <p className={styles.disclaimer}>Review suggestions against your content. Prices are checked again before publishing.</p>
    </section>
  )
}

export default memo(AiSuggestedBets)
